import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as CommentTriggerProps } from "~/components/article/article-comments-trigger";
import type { Props as ArticleReactionProps } from "~/components/article/article-reaction";
import { ArticleReaction } from "~/components/article/article-reaction";
import type { articleActionWidgetArticle$key } from "~/queries/__generated__/articleActionWidgetArticle.graphql";

import type { ContainerProps, ContentBookmarkProps } from "./styles";
import { Container, StyledCommentsTrigger, StyledContentBookmark } from "./styles";

type Variant = "default" | "photoEssay" | "magazines-style";

type CommentOption = {
  props: Partial<CommentTriggerProps>;
  type: "comment";
};

type BookmarkOption = {
  props: ContentBookmarkProps;
  type: "bookmark";
};

type ReactionOption = {
  props: Omit<ArticleReactionProps, "reference">;
  type: "reaction";
};

type Options = {
  container: ContainerProps;
  items: (CommentOption | BookmarkOption | ReactionOption)[];
};

const optionsMap: Record<Variant, Options> = {
  default: {
    container: { $orientation: "column" },
    items: [
      { props: { variant: "widgetLight" }, type: "comment" },
      { props: { $variant: "light" }, type: "bookmark" },
      { props: { variant: "light" }, type: "reaction" },
    ],
  },
  "magazines-style": {
    container: { $orientation: "row", $variant: "magazines-style" },
    items: [
      { props: { variant: "button" }, type: "comment" },
      { props: { $size: "small", $variant: "light" }, type: "bookmark" },
    ],
  },
  photoEssay: {
    container: { $orientation: "column" },
    items: [
      { props: { variant: "widgetDark" }, type: "comment" },
      { props: { $variant: "dark" }, type: "bookmark" },
    ],
  },
};

type Props = {
  className?: string;
  reference: articleActionWidgetArticle$key;
  variant?: Variant;
};

export const ArticleActionWidget: FunctionComponent<Props> = ({
  className,
  reference,
  variant = "default",
}) => {
  const article = useFragment(
    graphql`
      fragment articleActionWidgetArticle on Article {
        ...articleCommentsTriggerArticle
        ...articleReactionArticle
        entityId
        urlAlias
      }
    `,
    reference,
  );

  const handleRenderCommentsTrigger = ({ props }: CommentOption) => (
    <StyledCommentsTrigger
      key="comment-trigger"
      location="Article Side"
      reference={article}
      variant="widgetLight"
      {...props}
    />
  );

  const handleRenderContentBookmark = ({ props }: BookmarkOption) => (
    <StyledContentBookmark
      entityId={article.entityId}
      key="content-bookmark"
      location="side"
      urlAlias={article.urlAlias}
      {...props}
    />
  );

  const handleRenderArticleReaction = ({ props }: ReactionOption) => (
    <ArticleReaction {...props} key="content-reaction" reference={article} />
  );

  const { container, items } = optionsMap[variant];

  return (
    <Container {...container} className={className}>
      {items.map(item => {
        switch (item.type) {
          case "comment":
            return handleRenderCommentsTrigger(item);
          case "bookmark":
            return handleRenderContentBookmark(item);
          case "reaction":
            return handleRenderArticleReaction(item);
        }
      })}
    </Container>
  );
};
ArticleActionWidget.displayName = "ArticleActionWidget";
